// src/services/userService.js
import { toast } from 'react-toastify';
import { apiRequestSimple } from '../utils/apiHelper';
import AuthService from './authService';

class UserService {
  static get apiBase() {
    return window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
  }

  // Get all users (Admin only)
  static getAllUsers = async (page = 1, pageSize = 50) => {
    const result = await apiRequestSimple(`/api/user?page=${page}&pageSize=${pageSize}`, {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'Users could not be retrieved',
        data: [],
        pagination: null
      };
    }

    return {
      success: true,
      data: result.data || result.fullResponse?.data || [],
      pagination: result.fullResponse?.pagination || null
    };
  };

  // Get customer name by user ID
  static getCustomerNameByUserId = async (customerId) => {
    const result = await apiRequestSimple(`/api/user/getCustomerNameByUserId?customerId=${customerId}`, {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'Customer not found',
        data: null
      };
    }

    return {
      success: true,
      data: result.data
    };
  };

  // Get current user profile
  static getCurrentUserProfile = async () => {
    const user = await AuthService.getCurrentUserFromServer();
    if (!user) {
      return {
        success: false,
        message: 'Profile information could not be retrieved',
        data: null
      };
    }

    const roles = user.roles || [];
    const cachedUser = AuthService.getCurrentUser() || {};

    return {
      success: true,
      data: {
        ...cachedUser,
        ...user,
        role: roles[0] || user.role || cachedUser.role || '',
        roles
      }
    };
  };

  // Update user profile
  static updateProfile = async (profileData) => {
    const coreBase = window.APP_CONFIG?.coreAuthBase || 'http://localhost:8080/core';
    try {
      const response = await AuthService.authenticatedFetch(`${coreBase}/api/users/me/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });
      if (!response) return { success: false, message: 'Session expired. Please log in again.' };
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return { success: false, message: data.message || 'Profile could not be updated' };
      return { success: true, message: data.message || 'Profile updated successfully', data };
    } catch {
      return { success: false, message: 'Server error occurred' };
    }
  };

  // Get all active non-admin users (for seller selection)
  static getActiveNonAdminUsers = async () => {
    const result = await apiRequestSimple('/api/user/active-non-admin', {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'Active users could not be retrieved',
        data: []
      };
    }

    return {
      success: true,
      data: result.data || []
    };
  };

  // Change user password
  static changePassword = async (passwordData) => {
    const coreBase = window.APP_CONFIG?.coreAuthBase || 'http://localhost:8080/core';
    try {
      const response = await AuthService.authenticatedFetch(`${coreBase}/api/users/me/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passwordData)
      });
      if (!response) return { success: false, message: 'Session expired. Please log in again.' };
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return { success: false, message: data.message || 'Password could not be changed' };
      return { success: true, message: data.message || 'Password changed successfully', data };
    } catch {
      return { success: false, message: 'Server error occurred' };
    }
  };

  // Update user by admin (Admin only)
  static updateUserByAdmin = async (userId, userData) => {
    const result = await apiRequestSimple(`/api/user/admin/update/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      toast.error(result.message || 'User could not be updated');
      return {
        success: false,
        message: result.message || 'User could not be updated',
        data: null
      };
    }

    toast.success(result.message || 'User updated successfully');
    return {
      success: true,
      data: result.data,
      message: result.message || 'User updated successfully'
    };
  };

  // Change user status by admin (Admin only)
  static changeUserStatus = async (userId, status) => {
    const result = await apiRequestSimple(`/api/user/admin/status/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      toast.error(result.message || 'User status could not be updated');
      return {
        success: false,
        message: result.message || 'User status could not be updated',
        data: null
      };
    }

    toast.success(result.message || 'User status updated successfully');
    return {
      success: true,
      data: result.data,
      message: result.message || 'User status updated successfully'
    };
  };

  // Get user details by admin (Admin only)
  static getUserDetailsByAdmin = async (userId) => {
    const result = await apiRequestSimple(`/api/user/admin/${userId}`, {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'User details could not be retrieved',
        data: null
      };
    }

    return {
      success: true,
      data: result.data || result.fullResponse
    };
  };
}

export default UserService;
